import { ResizeHandle } from 'react-resizable';

import {
  axisValuesType,
  cropRatioValue,
  maxValuesType,
  sizeValuesType,
} from 'craft/components/SelectedToolbar/ImageCrop';

export const getRatioStringFromValue = (val: cropRatioValue) => {
  if (val.horizontal && val.vertical) {
    return `${val.shape}-${val.horizontal}-${val.vertical}`;
  }

  return undefined;
};

export const getRatioValueFromString = (str: string) => {
  const values = str.split('-');

  return {
    shape: values[0],
    horizontal: parseInt(values[1], 10),
    vertical: parseInt(values[2], 10),
  } as cropRatioValue;
};

interface getCropRatioDimsProps {
  width: number;
  height: number;
  ratio: number;
}

export const getCropRatioDims = ({ width, height, ratio }: getCropRatioDimsProps) => {
  let newWidth = width;
  let newHeight = height;
  const minSize = Math.min(width, height);

  if (ratio === 1) {
    newWidth = minSize;
    newHeight = minSize;
  } else if (height * ratio < width) {
    newWidth = newHeight * ratio;
  } else if (width / ratio < height) {
    newHeight = newWidth / ratio;
  }

  return {
    newWidth,
    newHeight,
  };
};

interface getResizeMaxDimsProps {
  handle: ResizeHandle;
  sizeValues: sizeValuesType;
  axisValues: axisValuesType;
  maxValues: maxValuesType;
  originalSizeValues: sizeValuesType;
  ratio?: number;
}

export const getResizeMaxDims = ({
  handle,
  sizeValues,
  axisValues,
  maxValues,
  originalSizeValues,
  ratio,
}: getResizeMaxDimsProps) => {
  let { maxWidth, maxHeight } = maxValues;

  if (handle.includes('nw')) {
    maxWidth = sizeValues.width + axisValues.x;
    maxHeight = sizeValues.height + axisValues.y;
  } else if (handle.includes('ne')) {
    maxWidth = originalSizeValues.width - axisValues.x;
    maxHeight = sizeValues.height + axisValues.y;
  } else if (handle.includes('se')) {
    maxWidth = originalSizeValues.width - axisValues.x;
    maxHeight = originalSizeValues.height - axisValues.y;
  } else if (handle.includes('sw')) {
    maxWidth = sizeValues.width + axisValues.x;
    maxHeight = originalSizeValues.height - axisValues.y;
  } else if (handle.includes('n')) {
    if (ratio) {
      const maxFullWidth = originalSizeValues.width - axisValues.x;
      let maxRatioHeight = sizeValues.height + axisValues.y;
      let maxRatioWidth = maxRatioHeight * ratio;
      if (maxRatioWidth > maxFullWidth) {
        maxRatioWidth = maxFullWidth;
        maxRatioHeight = maxRatioWidth / ratio;
      }
      maxWidth = maxRatioWidth;
      maxHeight = maxRatioHeight;
    } else {
      maxHeight = sizeValues.height + axisValues.y;
    }
  } else if (handle.includes('s')) {
    if (ratio) {
      const maxFullWidth = originalSizeValues.width - axisValues.x;
      let maxRatioHeight = originalSizeValues.height - axisValues.y;
      let maxRatioWidth = maxRatioHeight * ratio;
      if (maxRatioWidth > maxFullWidth) {
        maxRatioWidth = maxFullWidth;
        maxRatioHeight = maxRatioWidth / ratio;
      }
      maxWidth = maxRatioWidth;
      maxHeight = maxRatioHeight;
    } else {
      maxHeight = originalSizeValues.height - axisValues.y;
    }
  } else if (handle.includes('e')) {
    if (ratio) {
      const maxFullHeight = originalSizeValues.height - axisValues.y;
      let maxRatioWidth = originalSizeValues.width - axisValues.x;
      let maxRatioHeight = maxRatioWidth / ratio;
      if (maxRatioHeight > maxFullHeight) {
        maxRatioHeight = maxFullHeight;
        maxRatioWidth = maxRatioHeight * ratio;
      }
      maxWidth = maxRatioWidth;
      maxHeight = maxRatioHeight;
    } else {
      maxWidth = originalSizeValues.width - axisValues.x;
    }
  } else if (handle.includes('w')) {
    if (ratio) {
      const maxFullHeight = originalSizeValues.height - axisValues.y;
      let maxRatioWidth = sizeValues.width + axisValues.x;
      let maxRatioHeight = maxRatioWidth / ratio;
      if (maxRatioHeight > maxFullHeight) {
        maxRatioHeight = maxFullHeight;
        maxRatioWidth = maxRatioHeight * ratio;
      }
      maxWidth = maxRatioWidth;
      maxHeight = maxRatioHeight;
    } else {
      maxWidth = sizeValues.width + axisValues.x;
    }
  }

  return {
    maxWidth,
    maxHeight,
  };
};

interface cropToPropsProps {
  sizeValues: sizeValuesType;
  axisValues: axisValuesType;
  originalSizeValues: sizeValuesType;
  naturalSizeValues: sizeValuesType;
}

export const cropToProps = ({
  sizeValues,
  axisValues,
  originalSizeValues,
  naturalSizeValues,
}: cropToPropsProps) => {
  let top = axisValues.y || 0;
  let right = originalSizeValues.width - (axisValues.x + sizeValues.width) || 0;
  let bottom = originalSizeValues.height - (axisValues.y + sizeValues.height) || 0;
  let left = axisValues.x || 0;

  const widthRatio = naturalSizeValues.width / originalSizeValues.width;
  const heightRatio = naturalSizeValues.height / originalSizeValues.height;

  // cropping breaks if a value is less than 1 but more than 0,
  // so we are guaranteeing a minimum of 1 pixel if a side is cropped at all
  if (top !== 0) {
    top = Math.max(1, parseFloat((top * heightRatio).toFixed(4)));
  }
  if (bottom !== 0) {
    bottom = Math.max(1, parseFloat((bottom * heightRatio).toFixed(4)));
  }
  if (left !== 0) {
    left = Math.max(1, parseFloat((left * widthRatio).toFixed(4)));
  }
  if (right !== 0) {
    right = Math.max(1, parseFloat((right * widthRatio).toFixed(4)));
  }

  return {
    cropTop: `${top}`,
    cropRight: `${right}`,
    cropBottom: `${bottom}`,
    cropLeft: `${left}`,
  };
};

export const propsToCrop = (props: {
  width: number;
  height: number;
  widthRatio: number;
  heightRatio: number;
  top?: number;
  right?: number;
  bottom?: number;
  left?: number;
}) => {
  const {
    top: initialTop,
    right: initialRight,
    bottom: initialBottom,
    left: initialLeft,
    width,
    height,
    widthRatio,
    heightRatio,
  } = props;

  // if all 4 values are undefined, consider un-cropped
  if (
    typeof initialTop === 'undefined' &&
    typeof initialRight === 'undefined' &&
    typeof initialBottom === 'undefined' &&
    typeof initialLeft === 'undefined'
  ) {
    return {
      cropWidth: undefined,
      cropHeight: undefined,
      cropX: undefined,
      cropY: undefined,
    };
  }

  let top = initialTop || 0;
  let right = initialRight || 0;
  let bottom = initialBottom || 0;
  let left = initialLeft || 0;

  // if the image is not being displayed in the natural dimensions, adjust values
  if (widthRatio !== 1 || heightRatio !== 1) {
    if (top !== 0) {
      top = parseFloat((top / heightRatio).toFixed(4));
    }
    if (bottom !== 0) {
      bottom = parseFloat((bottom / heightRatio).toFixed(4));
    }
    if (left !== 0) {
      left = parseFloat((left / widthRatio).toFixed(4));
    }
    if (right !== 0) {
      right = parseFloat((right / widthRatio).toFixed(4));
    }
  }

  return {
    cropWidth: width - right - left,
    cropHeight: height - bottom - top,
    cropX: left,
    cropY: top,
  };
};
